// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./EditEmployeeDialog.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API CONFIG
import {
	getAPICall,
	putAPICall,
	postAPICall,
	deleteAPICall,
} from "../../../../config/apiCalls";

// MUI COMPONENTS
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Collapse,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

// CONTEXT
import { UserContext } from "../../../../context/UserContext";

// CONSTANTS
import { verifyEmailRegex } from "../../../../constants/regex";
import { formatPhoneNumber } from "../../../../constants/phone";

// REACT COMPONENT
const EditEmployeeDialog = (props) => {
	const employeeID = useContext(UserContext);
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [id, setId] = useState(0);

	// STATES
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [initials, setInitials] = useState("");

	const [oicID, setOicID] = useState(0);
	const [cellPhone, setCellPhone] = useState("");
	const [deskPhone, setDeskPhone] = useState("");
	const [email, setEmail] = useState("");

	const [hubspotID, setHubspotID] = useState("");
	const [companyCamID, setCompanyCamID] = useState("");
	const [ripplingID, setRipplingID] = useState("");
	const [bqeID, setBqeID] = useState("");
	const [teamsID, setTeamsID] = useState("");
	const [jobTitle, setJobTitle] = useState("");
	const [department, setDepartment] = useState("");
	const [isActive, setIsActive] = useState(1);
	const [employmentType, setEmploymentType] = useState("");
	const [hoursPerWeek, setHoursPerWeek] = useState("");

	const [roleID, setRoleID] = useState("");
	const [availableRoles, setAvailableRoles] = useState([]);

	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	// USE EFFECT
	useEffect(() => {
		if (props.open) {
			setId(props.recordToEdit.id || 0);

			setFirstName(props.recordToEdit.first_name || "");
			setLastName(props.recordToEdit.last_name || "");
			setInitials(props.recordToEdit.initials || "");

			setOicID(props.recordToEdit.oic_id || "");
			setCellPhone(
				formatPhoneNumber(props.recordToEdit.phone_number || "")
			);
			setDeskPhone(
				formatPhoneNumber(props.recordToEdit.desk_phone || "")
			);
			setEmail(props.recordToEdit.email || "");

			setHubspotID(props.recordToEdit.hubspot_id || "");
			setCompanyCamID(props.recordToEdit.company_cam_id || "");
			setRipplingID(props.recordToEdit.rippling_id || "");
			setBqeID(props.recordToEdit.bqe_id || "");
			setTeamsID(props.recordToEdit.teams_id || "");
			setJobTitle(
				props.recordToEdit.job_title_id
					? getJobTitleNameFromId(props.recordToEdit.job_title_id)
					: ""
			);
			setDepartment(
				props.recordToEdit.department_id
					? getDepartmentNameFromId(props.recordToEdit.department_id)
					: ""
			);
			setIsActive(props.recordToEdit.is_active);
			setEmploymentType(props.recordToEdit.employment_type || "");
			setHoursPerWeek(props.recordToEdit.hours_per_week ?? "");
		}
	}, [props.open, props.recordToEdit]);

	useEffect(() => {
		if (!props.open || !props.recordToEdit.id) {
			setRoleID("");
			return;
		}

		let cancelled = false;

		const fetchRoles = async () => {
			try {
				const [allRolesRes, employeeRolesRes] = await Promise.all([
					getAPICall(instance, accounts[0], "/api/permissions/roles/all"),
					getAPICall(
						instance,
						accounts[0],
						`/api/permissions/roles/byEmployee?employeeID=${props.recordToEdit.id}`
					),
				]);

				if (cancelled) return;

				setAvailableRoles(allRolesRes.data ?? []);
				setRoleID(employeeRolesRes.data?.[0]?.id ?? "");
			} catch (err) {
				console.error("Error fetching roles:", err);
			}
		};

		fetchRoles();

		return () => {
			cancelled = true;
		};
	}, [props.open, props.recordToEdit]);

	// INPUT HANDLERS
	const firstNameChanged = (event) => {
		setFirstName(event.target.value);
	};

	const lastNameChanged = (event) => {
		setLastName(event.target.value);
	};

	const initialsChanged = (event) => {
		setInitials(event.target.value);
	};

	const oicIDChanged = (event) => {
		setOicID(event.target.value);
	};

	const emailChanged = (event) => {
		setEmail(event.target.value);
	};

	const cellPhoneChanged = (event) => {
		setCellPhone(formatPhoneNumber(event.target.value));
	};

	const deskPhoneChanged = (event) => {
		setDeskPhone(formatPhoneNumber(event.target.value));
	};

	const hubspotIDChanged = (event) => {
		setHubspotID(event.target.value);
	};

	const companyCamIDChanged = (event) => {
		setCompanyCamID(event.target.value);
	};

	const ripplingIDChanged = (event) => {
		setRipplingID(event.target.value);
	};

	const bqeIDChanged = (event) => {
		setBqeID(event.target.value);
	};

	const teamsIDChanged = (event) => {
		setTeamsID(event.target.value);
	};

	const jobTitleChanged = (event) => {
		setJobTitle(event);
	};

	const departmentChanged = (event) => {
		setDepartment(event);
	};

	const isActiveChanged = (event) => {
		setIsActive(event.target.value);
	};

	const roleChanged = (event) => {
		setRoleID(event.target.value);
	};

	// SUBMIT HANDLER
	const submitButtonClicked = async () => {
		if (!validateInputFields()) return;
		if (id === 0) return;

		setIsLoading(true);

		let res = await putAPICall(
			instance,
			accounts[0],
			`/api/employees/update?id=${id}`,
			{
				firstName: firstName,
				lastName: lastName,
				initials: initials,
				oicID: oicID !== 0 ? oicID : null,
				cellPhone: cellPhone,
				deskPhone: deskPhone,
				email: email,
				hubspotID: hubspotID ? hubspotID : null,
				companyCamID: companyCamID ? companyCamID : null,
				ripplingID: ripplingID ? ripplingID : null,
				bqeID: bqeID ? bqeID : null,
				teamsID: teamsID ? teamsID : null,
				jobTitleID: jobTitle ? getJobTitleIdFromName(jobTitle) : null,
				departmentID: department ? getDepartmentIdFromName(department) : null,
				isActive: isActive,
				employmentType: employmentType || null,
				hoursPerWeek: hoursPerWeek !== "" ? hoursPerWeek : null,
				modifiedBy: employeeID,
			}
		);

		try {
			await deleteAPICall(instance, accounts[0], "/api/permissions/roles/deleteByEmployee", {
				employeeID: id,
			});
			if (roleID) {
				await postAPICall(instance, accounts[0], "/api/permissions/roles/add", {
					employeeID: id,
					roleID: roleID,
					modifiedBy: employeeID,
				});
			}
			props.onRoleChanged?.();
		} catch (err) {
			console.error("Error saving role:", err);
		}

		setIsLoading(false);

		formClosed();
		props.fetchEmployees();
	};

	const deleteButtonClicked = async () => {
		if (id === 0) return;

		setIsLoading(true);

		let res = await deleteAPICall(
			instance,
			accounts[0],
			`/api/employees/delete`,
			{
				employeeID: id,
			}
		);

		setIsLoading(false);

		formClosed();
		props.fetchEmployees();
	};

	// API FUNCTIONS

	// HELPER FUNCTIONS
	const resetForm = () => {
		setId(0);

		setFirstName("");
		setLastName("");
		setInitials("");

		setOicID(0);
		setCellPhone("");
		setDeskPhone("");
		setEmail("");

		setHubspotID("");
		setCompanyCamID("");
		setRipplingID("");
		setBqeID("");
		setTeamsID("");
		setJobTitle("");
		setDepartment("");
		setIsActive(1);
		setEmploymentType("");
		setHoursPerWeek("");
		setRoleID("");

		setAlertOpen(false);
		setAlertMessage("");
		setIsLoading(false);
	};

	const getJobTitleNameFromId = (jobTitleID) => {
		let jobTitle = props.jobTitles.find(
			(jobTitle) => jobTitle.id === jobTitleID
		);
		return jobTitle.name;
	};

	const getJobTitleIdFromName = (jobTitleName) => {
		let jobTitle = props.jobTitles.find(
			(jobTitle) => jobTitle.name === jobTitleName
		);
		return jobTitle.id;
	};

	const getDepartmentNameFromId = (departmentID) => {
		let department = props.departments.find(
			(department) => department.id === departmentID
		);
		return department ? department.name : "";
	};

	const getDepartmentIdFromName = (departmentName) => {
		let department = props.departments.find(
			(department) => department.name === departmentName
		);
		return department ? department.id : null;
	};

	const validateInputFields = () => {
		if (
			!firstName ||
			!lastName ||
			!initials ||
			!email ||
			!verifyEmailRegex(email)
		) {
			setAlertMessage("Please fill out all fields (first name, last name, email, initials).");
			setAlertOpen(true);
			return false;
		}

		if (hoursPerWeek !== "" && Number(hoursPerWeek) > 168) {
			setAlertMessage("Hours per week cannot exceed 168 (24 × 7).");
			setAlertOpen(true);
			return false;
		}

		return true;
	};

	const formClosed = () => {
		resetForm();
		props.editEmployeeDialogClosed();
	};

	// RENDER
	return (
		<Dialog
			id="editEmployeeDialog"
			open={props.open}
			fullWidth
			maxWidth="lg"
			onClose={formClosed}
		>
			<DialogTitle id="editEmployeeDialogTitle">
				Edit Employee
				<Button
					onClick={deleteButtonClicked}
					variant="contained"
					color="error"
				>
					Delete
				</Button>
			</DialogTitle>
			<DialogContent id="editEmployeeDialogContent">
				{isLoading && (
					<div className="loadingSpinnerContainer">
						<CircularProgress color="primary" />
					</div>
				)}
				{!isLoading && (
					<>
						<div className="editEmployeeDialogRow">
							<Collapse
								sx={{ flex: 1 }}
								id="editEmployeeCollapse"
								in={alertOpen}
							>
								<Alert
									id="editEmployeeAlert"
									severity="error"
									action={
										<IconButton
											id="editEmployeeCloseIconButton"
											aria-label="close"
											color="inherit"
											size="small"
											onClick={() => {
												setAlertOpen(false);
											}}
										>
											<CloseIcon
												id="editEmployeeCloseIcon"
												fontSize="inherit"
											/>
										</IconButton>
									}
								>
									{alertMessage}
								</Alert>
							</Collapse>
						</div>
						<div className="newEmployeeDialogRow">
							<TextField
								sx={{ flex: 3 }}
								id="newEmployeeFirstName"
								label="First Name"
								value={firstName}
								onChange={firstNameChanged}
							/>
							<TextField
								sx={{ flex: 3 }}
								id="newEmployeeLastName"
								label="Last Name"
								value={lastName}
								onChange={lastNameChanged}
							/>
							<TextField
								sx={{ flex: 1 }}
								id="newEmployeeInitials"
								label="Initials"
								value={initials}
								onChange={initialsChanged}
							/>
						</div>
						<div className="newEmployeeDialogRow">
							<FormControl sx={{ flex: 1 }}>
								<InputLabel id="newEmployeeOicIDLabel">
									OIC
								</InputLabel>
								<Select
									labelId="newEmployeeOicIDLabel"
									id="newEmployeeOicID"
									value={oicID}
									label="OIC"
									onChange={oicIDChanged}
								>
									{props.offices.map((office, index) => (
										<MenuItem key={index} value={office.id}>
											{office.state}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<TextField
								sx={{ flex: 3 }}
								id="newEmployeeEmail"
								label="Email"
								value={email}
								onChange={emailChanged}
								error={!verifyEmailRegex(email) && email !== ""}
							/>
							<TextField
								sx={{ flex: 3 }}
								id="newEmployeeCellPhone"
								label="Phone (Cell)"
								value={cellPhone}
								onChange={cellPhoneChanged}
								placeholder="(123) 456-7890"
								inputProps={{ inputMode: "numeric" }}
							/>
							<TextField
								sx={{ flex: 3 }}
								id="newDeskPhone"
								label="Phone (Desk)"
								value={deskPhone}
								onChange={deskPhoneChanged}
								placeholder="(123) 456-7890"
								inputProps={{ inputMode: "numeric" }}
							/>
						</div>
						<div className="newEmployeeDialogRow">
							<TextField
								sx={{ flex: 1 }}
								id="newEmployeeHubspotID"
								label="Hubspot ID"
								value={hubspotID}
								onChange={hubspotIDChanged}
							/>
							<TextField
								sx={{ flex: 1 }}
								id="newEmployeeCompanyCamID"
								label="CompanyCam ID"
								value={companyCamID}
								onChange={companyCamIDChanged}
							/>
							<TextField
								sx={{ flex: 1 }}
								id="newEmployeeRipplingID"
								label="Rippling ID"
								value={ripplingID}
								onChange={ripplingIDChanged}
							/>
							<TextField
								sx={{ flex: 1 }}
								id="newEmployeeBqeID"
								label="BQE ID"
								value={bqeID}
								onChange={bqeIDChanged}
							/>
						</div>
						<div className="newEmployeeDialogRow">
							<TextField
								sx={{ flex: 1 }}
								id="newEmployeeTeamsID"
								label="Teams ID"
								value={teamsID}
								onChange={teamsIDChanged}
							/>
						</div>
						<div className="newEmployeeDialogRow">
							<Autocomplete
								sx={{ flex: 1 }}
								id="newEmployeeJobTitle"
								options={props.jobTitles.map(
									(jobTitle) => jobTitle.name
								)}
								renderInput={(params) => (
									<TextField {...params} label="Job Title" />
								)}
								onChange={(event, value) => {
									jobTitleChanged(value);
								}}
								value={jobTitle}
							/>
							<Autocomplete
								sx={{ flex: 1 }}
								id="editEmployeeDepartment"
								options={props.departments.map(
									(department) => department.name
								)}
								renderInput={(params) => (
									<TextField {...params} label="Department" />
								)}
								onChange={(event, value) => {
									departmentChanged(value);
								}}
								value={department}
							/>
							<FormControl sx={{ flex: 1 }}>
								<InputLabel id="newEmployeeIsActiveLabel">
									Status
								</InputLabel>
								<Select
									labelId="newEmployeeIsActiveLabel"
									id="newEmployeeIsActive"
									value={isActive ? 1 : 0}
									label="Status"
									onChange={isActiveChanged}
								>
									<MenuItem value={1}>Active</MenuItem>
									<MenuItem value={0}>Inactive</MenuItem>
								</Select>
							</FormControl>
						</div>
						<div className="newEmployeeDialogRow">
							<FormControl sx={{ flex: 1 }}>
								<InputLabel id="editEmployeeEmploymentTypeLabel">
									Employment Type
								</InputLabel>
								<Select
									labelId="editEmployeeEmploymentTypeLabel"
									id="editEmployeeEmploymentType"
									value={employmentType}
									label="Employment Type"
									onChange={(e) => setEmploymentType(e.target.value)}
								>
									<MenuItem value=""><em>Not Set</em></MenuItem>
									<MenuItem value="full_time">Full Time</MenuItem>
									<MenuItem value="part_time">Part Time</MenuItem>
								</Select>
							</FormControl>
							<TextField
								sx={{ flex: 1 }}
								id="editEmployeeHoursPerWeek"
								label="Hours Per Week"
								type="number"
								value={hoursPerWeek}
								onChange={(e) => setHoursPerWeek(e.target.value)}
								inputProps={{ min: 0, max: 168, step: 0.5 }}
								helperText="Max 168 hrs/week (24 × 7)"
								error={hoursPerWeek !== "" && Number(hoursPerWeek) > 168}
							/>
							<FormControl sx={{ flex: 1 }}>
								<InputLabel id="editEmployeeRoleLabel">
									Role
								</InputLabel>
								<Select
									labelId="editEmployeeRoleLabel"
									id="editEmployeeRole"
									value={roleID}
									label="Role"
									onChange={roleChanged}
								>
									<MenuItem value="">
										<em>No Role</em>
									</MenuItem>
									{availableRoles.map((role) => (
										<MenuItem key={role.id} value={role.id}>
											{role.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</div>
					</>
				)}
				<div className="dialogButtons">
					<Button
						onClick={formClosed}
						variant="outlined"
						color="error"
					>
						Cancel
					</Button>
					<Button onClick={submitButtonClicked} variant="contained">
						Submit
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default EditEmployeeDialog;
